import { Action, createReducer, on } from '@ngrx/store';

import { Exhibitor } from '@planone/bau365/util-fair-data';

import * as DataAccessExhibitorsActions from './data-access-exhibitors.actions';

import { CombinedSearchResult, ExhibitorDirectoryStatus } from '../../../interfaces/exhibitor.interfaces';

export const DATA_ACCESS_EXHIBITORS_FEATURE_KEY = 'dataAccessExhibitors';

export interface ExhibitorFairDetailsState {
  activeExhibitorLoading?: boolean;
  activeExhibitorError?: string;
  activeExhibitorProductsLoading?: boolean;
  activeExhibitorProductsError?: string;
  activeExhibitorPostsLoading?: boolean;
  activeExhibitorPostsError?: string;
  activeExhibitorEventsLoading?: boolean;
  activeExhibitorEventsError?: string;
  activeExhibitorContactPerson?: string;
  nextCursor?: string;
  exhibitors: KeyedExhibitorsState;
  coExhibitors: KeyedCoExhibitorsState;
  directory: {
    letter: { [key: string]: CombinedSearchResult[] };
    hall: { [key: string]: CombinedSearchResult[] };
    search: { [key: string]: CombinedSearchResult[] };
  };
  directoryStatus: ExhibitorDirectoryStatus;
}

export const initialExhibitorFairDetailsState: ExhibitorFairDetailsState = {
  exhibitors: {},
  coExhibitors: {},
  directory: {
    letter: {},
    hall: {},
    search: {},
  },
  directoryStatus: {
    first: false,
    all: false,
    failed: false,
    loaded: {
      letters: [],
      halls: [],
      availableHalls: [],
      availableLetters: [
        'A',
        'B',
        'C',
        'D',
        'E',
        'F',
        'G',
        'H',
        'I',
        'J',
        'K',
        'L',
        'M',
        'N',
        'O',
        'P',
        'Q',
        'R',
        'S',
        'T',
        'U',
        'V',
        'W',
        'X',
        'Y',
        'Z',
        '#',
      ],
      searchTerms: [],
    },
  },
};

export interface ExhibitorsState {
  [id: string]: ExhibitorFairDetailsState;
}

export interface KeyedExhibitorsState {
  [id: string]: Exhibitor;
}

export interface KeyedCoExhibitorsState {
  [id: string]: string[];
}

export interface DataAccessExhibitorsPartialState {
  readonly [DATA_ACCESS_EXHIBITORS_FEATURE_KEY]: ExhibitorsState;
}

export const initialExhibitorsState: ExhibitorsState = {};

const dataAccessExhibitorsReducer = createReducer(
  initialExhibitorsState,
  on(DataAccessExhibitorsActions.exhibitorOpened, (state, { exhibitorId, fairId }) => {
    const loaded = typeof state[fairId]?.exhibitors[exhibitorId] !== 'undefined';
    const productsLoaded = loaded && typeof state[fairId]?.exhibitors[exhibitorId].products !== 'undefined';
    const postsLoaded = loaded && typeof state[fairId]?.exhibitors[exhibitorId].posts !== 'undefined';

    return {
      ...state,
      [fairId]: {
        ...(state[fairId] ?? initialExhibitorFairDetailsState),
        activeExhibitorLoading: !loaded,
        activeExhibitorError: undefined,
        activeExhibitorProductsLoading: !productsLoaded,
        activeExhibitorProductsError: undefined,
        activeExhibitorPostsLoading: !postsLoaded,
        activeExhibitorPostsError: undefined,
        activeExhibitorContactPerson: undefined,
      },
    };
  }),
  on(DataAccessExhibitorsActions.exhibitorContactPersonOpened, (state, { exhibitorId, contactPersonIndex, fairId }) => {
    const loaded = typeof state[fairId]?.exhibitors[exhibitorId] !== 'undefined';
    const productsLoaded = loaded && typeof state[fairId]?.exhibitors[exhibitorId].products !== 'undefined';
    const postsLoaded = loaded && typeof state[fairId]?.exhibitors[exhibitorId].posts !== 'undefined';
    const contactPersonLoaded =
      loaded &&
      typeof state[fairId]?.exhibitors[exhibitorId].contact_persons !== 'undefined' &&
      (state[fairId]?.exhibitors[exhibitorId].contact_persons?.filter((value) => value.id === contactPersonIndex)
        .length ??
        0 > 0);

    return {
      ...state,
      [fairId]: {
        ...(state[fairId] ?? initialExhibitorFairDetailsState),
        activeExhibitorLoading: !loaded,
        activeExhibitorError: undefined,
        activeExhibitorProductsLoading: !productsLoaded,
        activeExhibitorProductsError: undefined,
        activeExhibitorPostsLoading: !postsLoaded,
        activeExhibitorPostsError: undefined,
        activeExhibitorContactPerson: !contactPersonLoaded ? undefined : contactPersonIndex,
      },
    };
  }),
  on(DataAccessExhibitorsActions.exhibitorLoadSucceeded, (state, { exhibitor, contactPersonIndex, fairId }) => ({
    ...state,
    [fairId]: {
      ...(state[fairId] ?? initialExhibitorFairDetailsState),
      activeExhibitorLoading: false,
      activeExhibitorError: undefined,
      activeExhibitorContactPerson: contactPersonIndex,
      exhibitors: {
        ...(state[fairId]?.exhibitors ?? initialExhibitorFairDetailsState.exhibitors),
        [exhibitor.id]: exhibitor,
      },
    },
  })),
  on(DataAccessExhibitorsActions.exhibitorParentLoadSucceeded, (state, { exhibitor, fairId }) => ({
    ...state,
    [fairId]: {
      ...(state[fairId] ?? initialExhibitorFairDetailsState),
      exhibitors: {
        ...(state[fairId]?.exhibitors ?? initialExhibitorFairDetailsState.exhibitors),
        [exhibitor.id]: exhibitor,
      },
    },
  })),
  on(DataAccessExhibitorsActions.exhibitorCoExhibitorLoadSucceeded, (state, { exhibitor_id, exhibitors, fairId }) => {
    let updatedState: ExhibitorsState = state;

    exhibitors
      .filter((exhibitor) => !state[fairId]?.exhibitors[exhibitor.id])
      .forEach((exhibitor) => {
        updatedState = {
          ...updatedState,
          [fairId]: {
            ...(updatedState[fairId] ?? initialExhibitorFairDetailsState),
            exhibitors: {
              ...(updatedState[fairId] ?? initialExhibitorFairDetailsState).exhibitors,
              ...{ [exhibitor.id]: exhibitor },
            },
          },
        };
      });

    updatedState = {
      ...updatedState,
      [fairId]: {
        ...(updatedState[fairId] ?? initialExhibitorFairDetailsState),
        coExhibitors: {
          ...(updatedState[fairId] ?? initialExhibitorFairDetailsState).coExhibitors,
          ...{ [exhibitor_id]: exhibitors.map((exhibitor: Exhibitor) => exhibitor.id) },
        },
      },
    };

    return updatedState;
  }),
  on(DataAccessExhibitorsActions.exhibitorLoadFailed, (state, { error, fairId }) => ({
    ...state,
    [fairId]: {
      ...(state[fairId] ?? initialExhibitorFairDetailsState),
      activeExhibitorError: error,
      activeExhibitorLoading: false,
    },
  })),
  on(DataAccessExhibitorsActions.exhibitorProductsLoadSucceeded, (state, { fairId }) => ({
    ...state,
    [fairId]: {
      ...(state[fairId] ?? initialExhibitorFairDetailsState),
      activeExhibitorProductsLoading: false,
      activeExhibitorProductsError: undefined,
    },
  })),
  on(DataAccessExhibitorsActions.exhibitorProductsLoadNext, (state, { fairId }) => ({
    ...state,
    [fairId]: {
      ...(state[fairId] ?? initialExhibitorFairDetailsState),
      activeExhibitorProductsError: undefined,
      activeExhibitorProductsLoading: true,
    },
  })),
  on(DataAccessExhibitorsActions.exhibitorProductsLoadFailed, (state, { error, fairId }) => ({
    ...state,
    [fairId]: {
      ...(state[fairId] ?? initialExhibitorFairDetailsState),
      activeExhibitorProductsError: error,
      activeExhibitorProductsLoading: false,
    },
  })),
  on(DataAccessExhibitorsActions.exhibitorPostsLoadSucceeded, (state, { fairId }) => ({
    ...state,
    [fairId]: {
      ...(state[fairId] ?? initialExhibitorFairDetailsState),
      activeExhibitorPostsLoading: false,
      activeExhibitorPostsError: undefined,
    },
  })),
  on(DataAccessExhibitorsActions.exhibitorPostsLoadNext, (state, { fairId }) => ({
    ...state,
    [fairId]: {
      ...(state[fairId] ?? initialExhibitorFairDetailsState),
      activeExhibitorPostsError: undefined,
      activeExhibitorPostsLoading: true,
    },
  })),
  on(DataAccessExhibitorsActions.exhibitorPostsLoadFailed, (state, { error, fairId }) => ({
    ...state,
    [fairId]: {
      ...(state[fairId] ?? initialExhibitorFairDetailsState),
      activeExhibitorPostsError: error,
      activeExhibitorPostsLoading: false,
    },
  })),
  on(DataAccessExhibitorsActions.exhibitorEventsLoadSucceeded, (state, { fairId }) => ({
    ...state,
    [fairId]: {
      ...(state[fairId] ?? initialExhibitorFairDetailsState),
      activeExhibitorEventsLoading: false,
      activeExhibitorEventsError: undefined,
    },
  })),
  on(DataAccessExhibitorsActions.exhibitorEventsLoadNext, (state, { fairId }) => ({
    ...state,
    [fairId]: {
      ...(state[fairId] ?? initialExhibitorFairDetailsState),
      activeExhibitorEventsError: undefined,
      activeExhibitorEventsLoading: true,
    },
  })),
  on(DataAccessExhibitorsActions.exhibitorEventsLoadFailed, (state, { error, fairId }) => ({
    ...state,
    [fairId]: {
      ...(state[fairId] ?? initialExhibitorFairDetailsState),
      activeExhibitorEventsError: error,
      activeExhibitorEventsLoading: false,
    },
  })),
  on(DataAccessExhibitorsActions.exhibitorDirectoryOpened, (state, { letter, fairId }) => {
    if (!state[fairId]?.directoryStatus.loaded.letters.includes(letter)) {
      return {
        ...state,
        [fairId]: {
          ...(state[fairId] ?? initialExhibitorFairDetailsState),
          directoryStatus: {
            ...(state[fairId]?.directoryStatus ?? initialExhibitorFairDetailsState.directoryStatus),
            first: false,
            all: false,
            failed: false,
          },
        },
      };
    }

    return {
      ...state,
      [fairId]: initialExhibitorFairDetailsState,
    };
  }),
  on(DataAccessExhibitorsActions.exhibitorDirectoryLoadNext, (state, { letter, results, fairId }) => ({
    ...state,
    [fairId]: {
      ...(state[fairId] ?? initialExhibitorFairDetailsState),
      directory: {
        ...(state[fairId] ?? initialExhibitorFairDetailsState).directory,
        letter: {
          ...(state[fairId] ?? initialExhibitorFairDetailsState).directory.letter,
          [letter]: [
            ...((state[fairId] ?? initialExhibitorFairDetailsState).directory.letter[letter] ?? []),
            ...results,
          ],
        },
      },
      directoryStatus: {
        ...(state[fairId] ?? initialExhibitorFairDetailsState).directoryStatus,
        first: true,
        all: false,
        failed: false,
      },
    },
  })),
  on(DataAccessExhibitorsActions.exhibitorDirectoryLoadSucceeded, (state, { letter, results, fairId }) => ({
    ...state,
    [fairId]: {
      ...(state[fairId] ?? initialExhibitorFairDetailsState),
      directory: {
        ...(state[fairId] ?? initialExhibitorFairDetailsState).directory,
        letter: {
          ...(state[fairId] ?? initialExhibitorFairDetailsState).directory.letter,
          [letter]: [
            ...((state[fairId] ?? initialExhibitorFairDetailsState).directory.letter[letter] ?? []),
            ...results,
          ],
        },
      },
      directoryStatus: {
        first: true,
        all: true,
        failed: false,
        loaded: {
          ...(state[fairId] ?? initialExhibitorFairDetailsState).directoryStatus.loaded,
          letters: [...(state[fairId] ?? initialExhibitorFairDetailsState).directoryStatus.loaded.letters, letter],
        },
      },
    },
  })),
  on(DataAccessExhibitorsActions.exhibitorDirectoryLoadFailed, (state, { fairId }) => ({
    ...state,
    [fairId]: {
      ...(state[fairId] ?? initialExhibitorFairDetailsState),
      directoryStatus: {
        ...(state[fairId] ?? initialExhibitorFairDetailsState).directoryStatus,
        first: false,
        all: false,
        failed: true,
      },
    },
  })),
  on(DataAccessExhibitorsActions.exhibitorDirectoryHallsInitSucceeded, (state, { halls, fairId }) => ({
    [fairId]: {
      ...(state[fairId] ?? initialExhibitorFairDetailsState),
      directoryStatus: {
        ...(state[fairId] ?? initialExhibitorFairDetailsState).directoryStatus,
        loaded: {
          ...(state[fairId] ?? initialExhibitorFairDetailsState).directoryStatus.loaded,
          availableHalls: halls,
        },
      },
    },
  })),
  on(DataAccessExhibitorsActions.exhibitorDirectoryHallsOpened, (state, { hall, fairId }) => {
    if (!state[fairId]?.directoryStatus.loaded.halls.includes(hall)) {
      return {
        ...state,
        [fairId]: {
          ...(state[fairId] ?? initialExhibitorFairDetailsState),
          directoryStatus: {
            ...(state[fairId]?.directoryStatus ?? initialExhibitorFairDetailsState.directoryStatus),
            first: false,
            all: false,
            failed: false,
          },
        },
      };
    }

    return {
      ...state,
      [fairId]: state[fairId] ?? initialExhibitorFairDetailsState,
    };
  }),
  on(DataAccessExhibitorsActions.exhibitorDirectoryHallLoadSucceeded, (state, { hall, results, fairId }) => ({
    ...state,
    [fairId]: {
      ...(state[fairId] ?? initialExhibitorFairDetailsState),
      directory: {
        ...(state[fairId] ?? initialExhibitorFairDetailsState).directory,
        hall: {
          ...(state[fairId] ?? initialExhibitorFairDetailsState).directory.hall,
          [hall]: [...((state[fairId] ?? initialExhibitorFairDetailsState).directory.hall[hall] ?? []), ...results],
        },
      },
      directoryStatus: {
        first: true,
        all: true,
        failed: false,
        loaded: {
          ...(state[fairId] ?? initialExhibitorFairDetailsState).directoryStatus.loaded,
          halls: [...(state[fairId] ?? initialExhibitorFairDetailsState).directoryStatus.loaded.halls, hall],
        },
      },
    },
  })),
  on(DataAccessExhibitorsActions.exhibitorDirectoryHallLoadNext, (state, { hall, results, fairId }) => ({
    ...state,
    [fairId]: {
      ...(state[fairId] ?? initialExhibitorFairDetailsState),
      directory: {
        ...(state[fairId] ?? initialExhibitorFairDetailsState).directory,
        hall: {
          ...(state[fairId] ?? initialExhibitorFairDetailsState).directory.hall,
          [hall]: [...((state[fairId] ?? initialExhibitorFairDetailsState).directory.hall[hall] ?? []), ...results],
        },
      },
      directoryStatus: {
        ...(state[fairId] ?? initialExhibitorFairDetailsState).directoryStatus,
        first: true,
        all: false,
        failed: false,
      },
    },
  })),
  on(DataAccessExhibitorsActions.exhibitorDirectorySearchOpened, (state, { fairId }) => ({
    ...state,
    [fairId]: {
      ...(state[fairId] ?? initialExhibitorFairDetailsState),
      directoryStatus: {
        ...(state[fairId] ?? initialExhibitorFairDetailsState).directoryStatus,
        first: false,
      },
    },
  })),
  on(
    DataAccessExhibitorsActions.exhibitorDirectorySearchLoadSucceeded,
    (state, { searchTerm, results, fairId, count }) => ({
      ...state,
      [fairId]: {
        ...(state[fairId] ?? initialExhibitorFairDetailsState),
        directory: {
          ...(state[fairId] ?? initialExhibitorFairDetailsState).directory,
          search: {
            ...(state[fairId] ?? initialExhibitorFairDetailsState).directory.search,
            [searchTerm]: results,
          },
        },
        directoryStatus: {
          first: true,
          all: true,
          failed: false,
          loaded: {
            ...(state[fairId] ?? initialExhibitorFairDetailsState).directoryStatus.loaded,
            searchTerms: [
              ...(state[fairId] ?? initialExhibitorFairDetailsState).directoryStatus.loaded.searchTerms,
              searchTerm,
            ],
          },
          searchStatus: {
            loadedResults: results.length,
            count: count,
            searchTerm: searchTerm,
          },
        },
      },
    })
  ),
  on(
    DataAccessExhibitorsActions.exhibitorDirectorySearchLoadNextSucceeded,
    (state, { searchTerm, results, fairId }) => ({
      ...state,
      [fairId]: {
        ...(state[fairId] ?? initialExhibitorFairDetailsState),
        directory: {
          ...(state[fairId] ?? initialExhibitorFairDetailsState).directory,
          search: {
            ...(state[fairId] ?? initialExhibitorFairDetailsState).directory.search,
            [searchTerm]: [
              ...((state[fairId] ?? initialExhibitorFairDetailsState).directory.search[searchTerm] ?? []),
              ...results,
            ],
          },
        },
        directoryStatus: {
          first: true,
          all: true,
          failed: false,
          loaded: {
            ...(state[fairId] ?? initialExhibitorFairDetailsState).directoryStatus.loaded,
            searchTerms: [
              ...(state[fairId] ?? initialExhibitorFairDetailsState).directoryStatus.loaded.searchTerms,
              searchTerm,
            ],
          },
          searchStatus: {
            ...(state[fairId] ?? initialExhibitorFairDetailsState).directoryStatus.searchStatus,
            loadedResults:
              ((state[fairId] ?? initialExhibitorFairDetailsState).directoryStatus.searchStatus?.loadedResults ?? 0) +
              results.length,
          },
        },
      },
    })
  )
);

export function exhibitorsReducer(state: ExhibitorsState | undefined, action: Action) {
  return dataAccessExhibitorsReducer(state, action);
}
